import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Alert, Toggle, InputGroup } from '@storable/canaveral-components';
import { Row } from 'react-bootstrap';
import Icon from 'modules/react/components/stateless/Icon';
import OGTags from './OGTags';
import style from './AdvancedSettings.scss';

const AdvancedSettings = ({
  canonical,
  children,
  description,
  disableEnUsPage,
  nonDefaultLanguages,
  metaTags,
  noIndex,
  syncOGTags,
  title,
  toggleLangNoIndex,
  togglePageNoIndex,
  toggleSyncOGTags,
  updateCanonical,
  updateMetaTags,
  validCanonical,
}) => {
  const canonicalValidationProps = {
    id: validCanonical ? 'success' : 'warning',
    className: cx(style.canonicalIcon, validCanonical ? style.validIcon : style.invalidIcon),
    label: 'canonical validation state',
  };

  return (
    <div className={style.advancedArea}>
      <div className={style.advancedSettingsHeader}>
        Advanced Settings
      </div>
      <Alert context="warning" className={style.alertContainer}>
        <i className={`fa fa-exclamation-triangle ${style.warningIcon}`} />
        <div className={style.alertText}>
          These settings are for advanced users.
          We don't recommend using them unless you know exactly what you are doing.
        </div>
      </Alert>
      <div className={style.toggleContainer}>
        <div className={style.toggle}>
          <Toggle
            readOnly
            onClick={disableEnUsPage ? undefined : togglePageNoIndex}
            checked={disableEnUsPage || noIndex}
            disabled={disableEnUsPage}
          />
        </div>
        <div className={style.textContainer}>
          <div className={style.header}>
            Hide From Search Results
          </div>
          <div className={style.description}>
            This marks this page as
            <span className={style.italicText}> no index </span>
            and excludes it from your site map.
          </div>
        </div>
      </div>
      {nonDefaultLanguages.length > 0 && children.length > 0 &&
        <Row className={cx(style.toggleContainer, style.enUsContainer)}>
          <div
            className={style.toggle}
          >
            <Toggle
              readOnly
              onClick={toggleLangNoIndex}
              checked={disableEnUsPage}
            />
          </div>
          <div className={style.textContainer}>
            <div className={style.header}>
              Disable EN-US Page
            </div>
            <div className={style.description}>
              This setting marks the EN-US page as
              <span className={style.italicText}> no index </span>
              and hides it from navigation.
            </div>
          </div>
        </Row>
      }
      <div className={style.optionContainer}>
        <div className={style.header}>
          Canonical URL
        </div>
        <div className={style.info}>
          This is the preferred URL for this page.  If left blank, it will default
          to the URL field or custom URL field, if in use.
        </div>
        <div>
          <InputGroup className={style.longInput}>
            <InputGroup.Text
              type="text"
              onChange={updateCanonical}
              value={canonical || ''}
              className={cx({ [style.error]: !validCanonical })}
            />
          </InputGroup>
        </div>
        {canonical && <Icon {...canonicalValidationProps} />}
        {canonical && !validCanonical && (
          <span className={style.canonicalFeedback}>
            URL must begin with "http://" or "https://" and only contain a-z, numbers, "/", "_" and "-".
          </span>
        )}
      </div>
      <div className={style.optionContainer}>
        <OGTags
          title={title}
          description={description}
          metaTags={metaTags}
          syncOGTags={syncOGTags}
          updateMetaTags={updateMetaTags}
          toggleSyncOGTags={toggleSyncOGTags}
        />
      </div>
    </div>
  );
};

AdvancedSettings.propTypes = {
  canonical: PropTypes.string,
  children: PropTypes.array,
  description: PropTypes.string,
  disableEnUsPage: PropTypes.bool,
  nonDefaultLanguages: PropTypes.array,
  metaTags: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string.isRequired,
    content: PropTypes.string,
  })),
  noIndex: PropTypes.bool,
  syncOGTags: PropTypes.bool,
  title: PropTypes.string,
  toggleLangNoIndex: PropTypes.func.isRequired,
  togglePageNoIndex: PropTypes.func.isRequired,
  toggleSyncOGTags: PropTypes.func.isRequired,
  updateCanonical: PropTypes.func.isRequired,
  updateMetaTags: PropTypes.func.isRequired,
  validCanonical: PropTypes.bool.isRequired,
};

AdvancedSettings.defaultProps = {
  nonDefaultLanguages: [],
  children: [],
};

export default AdvancedSettings;
